import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import {
  MODULE_META,
  MODULES,
  MODULE_REQUIRES,
  dependentsOf,
  type ModuleKey,
  type ModuleState,
} from '@esa/shared';
import { api } from '../../lib/api.js';
import { Badge, Button, Callout, Input, Panel, Segmented, Spinner, cn } from '../../ui/index.js';
import { useConfigWrite } from './useConfigWrite.js';

/**
 * Which features this group has, and who can see the ones still in pilot.
 *
 * Platform admin only — the tab is not rendered for anyone else, and the API
 * refuses `tenant.manage` independently. A group that could switch its own
 * modules on would make "not all tenants need all features" a suggestion
 * rather than a decision.
 */

interface Row {
  module: ModuleKey;
  state: ModuleState;
  note: string | null;
  /** Prerequisites that are off. Non-empty means this row is inert. */
  suppressedBy: ModuleKey[];
  pilots: { oid: string; name: string | null; email: string }[];
}

const STATE_TONE: Record<ModuleState, 'neutral' | 'warn' | 'ok'> = {
  off: 'neutral',
  pilot: 'warn',
  on: 'ok',
};

export function ModulesTab({ tenant }: { tenant: string }) {
  const write = useConfigWrite(tenant);
  const q = useQuery({
    queryKey: ['admin-modules', tenant],
    queryFn: () =>
      api.get<{ modules: Row[] }>(`/api/v1/tenants/${tenant}/admin/modules`),
    enabled: Boolean(tenant),
  });

  if (q.isLoading) return <Spinner />;
  if (q.error) return <p className="text-xs text-ink-tertiary">Modules could not be loaded.</p>;

  const rows = q.data?.modules ?? [];
  const on = rows.filter((r) => r.state === 'on').length;
  const piloting = rows.filter((r) => r.state === 'pilot').length;
  const inert = rows.filter((r) => r.suppressedBy.length > 0);

  return (
    <Panel
      title="Features"
      subtitle={`${on} on · ${piloting} in pilot · ${rows.length - on - piloting} off`}
      footer={
        <p className="text-2xs leading-relaxed text-ink-tertiary">
          Tickets are not listed: a group without ticketing is not a configuration. Switching a
          feature off makes its screens disappear and its API answer{' '}
          <strong className="text-ink-secondary">not found</strong> — nothing is deleted, and
          turning it back on restores it exactly as it was.
        </p>
      }
    >
      <Callout tone="info" className="mb-4">
        <strong>Pilot</strong> means the feature is live only for the people you name — everyone
        else in the group sees it as though it were off. That is how a feature reaches a few
        agents before it reaches the desk.
      </Callout>

      {/* Named at the top, not only on the row. Something switched on and
          doing nothing is the state worth interrupting for; finding it means
          scrolling past eight rows that are fine. */}
      {inert.length > 0 && (
        <Callout tone="warn" className="mb-4">
          {inert.map((r) => MODULE_META[r.module].label).join(' and ')}{' '}
          {inert.length === 1 ? 'is' : 'are'} switched on but doing nothing, because{' '}
          {[...new Set(inert.flatMap((r) => r.suppressedBy))]
            .map((k) => MODULE_META[k].label)
            .join(' and ')}{' '}
          is off. Switch it back on, or switch{' '}
          {inert.length === 1 ? 'this' : 'these'} off so the setting says what is true.
        </Callout>
      )}

      <ul className="space-y-2">
        {MODULES.map((key) => {
          const row = rows.find((r) => r.module === key);
          if (!row) return null;
          return <ModuleRow key={key} tenant={tenant} row={row} write={write} />;
        })}
      </ul>
    </Panel>
  );
}

function ModuleRow({
  tenant,
  row,
  write,
}: {
  tenant: string;
  row: Row;
  write: ReturnType<typeof useConfigWrite>;
}) {
  const meta = MODULE_META[row.module];
  const [email, setEmail] = useState('');
  const requires = MODULE_REQUIRES[row.module] ?? [];
  const dependents = dependentsOf(row.module);
  const suppressed = row.suppressedBy.length > 0;

  const setState = (state: ModuleState) =>
    write.mutate({
      method: 'put',
      path: `/api/v1/tenants/${tenant}/admin/modules/${row.module}`,
      body: { state },
      done: `${meta.label} is now ${state}`,
    });

  return (
    <li className="rounded-md border border-line-subtle bg-surface p-3">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-ink">{meta.label}</span>
            {/* The stored state AND whether it is in force. Showing only the
                first would put a green "on" on a feature that does nothing. */}
            <Badge tone={suppressed ? 'neutral' : STATE_TONE[row.state]}>{row.state}</Badge>
            {suppressed && <Badge tone="warn">doing nothing</Badge>}
          </div>
          <p className="mt-1 text-xs leading-relaxed text-ink-secondary">{meta.description}</p>
          {/* What actually disappears. A toggle whose effect is not stated is
              a toggle nobody dares throw. */}
          <p className="mt-1 text-2xs leading-relaxed text-ink-tertiary">{meta.affects}</p>

          {requires.length > 0 && (
            <p className="mt-1.5 text-2xs text-ink-tertiary">
              Needs {requires.map((k) => MODULE_META[k].label).join(' and ')}
              {suppressed && (
                <span className="text-warn">
                  {' '}
                  — which {row.suppressedBy.length === 1 ? 'is' : 'are'} off, so this is having
                  no effect
                </span>
              )}
            </p>
          )}
          {/* What switching THIS off would take with it. The admin should not
              have to discover that by doing it. */}
          {dependents.length > 0 && row.state !== 'off' && (
            <p className="mt-1 text-2xs text-ink-tertiary">
              Switching this off also stops{' '}
              {dependents.map((k) => MODULE_META[k].label).join(' and ')}
            </p>
          )}
        </div>

        <Segmented
          value={row.state}
          onChange={setState}
          options={[
            { value: 'off', label: 'Off' },
            { value: 'pilot', label: 'Pilot' },
            { value: 'on', label: 'On' },
          ]}
        />
      </div>

      {row.state === 'pilot' && (
        <div className="mt-3 border-t border-line-subtle pt-3">
          <p className="mb-2 text-2xs text-ink-tertiary">
            {row.pilots.length === 0
              ? 'Nobody yet — while this list is empty the feature is off for everyone.'
              : `${row.pilots.length} ${row.pilots.length === 1 ? 'person' : 'people'} can see this`}
          </p>

          <ul className="mb-2 flex flex-wrap gap-1.5">
            {row.pilots.map((p) => (
              <li
                key={p.oid}
                className={cn(
                  'inline-flex items-center gap-1.5 rounded-full bg-surface-sunken px-2 py-0.5',
                  'text-2xs text-ink-secondary ring-1 ring-line',
                )}
              >
                {p.name ?? p.email}
                <button
                  type="button"
                  aria-label={`Remove ${p.name ?? p.email}`}
                  onClick={() =>
                    write.mutate({
                      method: 'del',
                      path: `/api/v1/tenants/${tenant}/admin/modules/${row.module}/pilots/${p.oid}`,
                      done: `${p.name ?? p.email} removed from the ${meta.label} pilot`,
                    })
                  }
                  className="text-ink-tertiary transition-colors hover:text-danger"
                >
                  <X aria-hidden className="h-3 w-3" />
                </button>
              </li>
            ))}
          </ul>

          <form
            className="flex items-center gap-2"
            onSubmit={(e) => {
              e.preventDefault();
              if (email.trim() === '') return;
              write.mutate({
                method: 'post',
                path: `/api/v1/tenants/${tenant}/admin/modules/${row.module}/pilots`,
                body: { email: email.trim() },
                done: `Added to the ${meta.label} pilot`,
              });
              setEmail('');
            }}
          >
            <Input
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="someone@esassoc.com"
              className="max-w-xs"
            />
            <Button type="submit" size="sm" variant="ghost" disabled={email.trim() === ''}>
              <Plus aria-hidden className="h-3.5 w-3.5" />
              Add
            </Button>
          </form>
        </div>
      )}
    </li>
  );
}
